/**
 * engineKeyService -- HMAC key self-service for the deid-aws risk engine.
 *
 * Backend routes (BB-gated):
 *   GET  /v1/risk/keys/current    -- key metadata (no secret)
 *   POST /v1/risk/keys/generate   -- first-time mint (idempotent)
 *   POST /v1/risk/keys/regenerate -- rotate, invalidates old key
 *
 * The `secret` field is returned ONCE on fresh mint or regenerate and
 * never again. Callers must surface it immediately.
 */

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

export interface EngineKeyMeta {
  key_id: string | null;
  created_at?: string;
  last_used_at?: string | null;
}

export interface EngineKeyMinted {
  key_id: string;
  secret: string;
  created_at: string;
  already_existed?: false;
}

export interface EngineKeyIdempotent {
  key_id: string;
  created_at: string | null;
  already_existed: true;
}

export type GenerateResult = EngineKeyMinted | EngineKeyIdempotent;

export interface EngineKeyRotated {
  key_id: string;
  secret: string;
  created_at: string;
  regenerated_from: string | null;
}

function authHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err: any = new Error(
      (body as any)?.error?.message ||
        (typeof (body as any)?.error === 'string' ? (body as any).error : null) ||
        `HTTP ${res.status}`
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json() as Promise<T>;
}

export async function getCurrent(token: string): Promise<EngineKeyMeta> {
  const res = await fetch(`${API_BASE}/v1/risk/keys/current`, {
    headers: authHeaders(token),
  });
  return handleResponse<EngineKeyMeta>(res);
}

export async function generate(token: string): Promise<GenerateResult> {
  const res = await fetch(`${API_BASE}/v1/risk/keys/generate`, {
    method: 'POST',
    headers: authHeaders(token),
    body: '{}',
  });
  return handleResponse<GenerateResult>(res);
}

export async function regenerate(token: string): Promise<EngineKeyRotated> {
  const res = await fetch(`${API_BASE}/v1/risk/keys/regenerate`, {
    method: 'POST',
    headers: authHeaders(token),
    body: '{}',
  });
  return handleResponse<EngineKeyRotated>(res);
}
